import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthProvider";
import { FEATURE_FLAGS, FEATURE_KEYS, FeatureKey, FeatureFlagRow } from "@/lib/featureFlags";

interface FeatureFlagsContextValue {
	flags: Record<FeatureKey, boolean>;
	loading: boolean;
	error: Error | null;
	refresh: () => Promise<void>;
}

const buildDefaultFlags = (): Record<FeatureKey, boolean> => {
	const out = {} as Record<FeatureKey, boolean>;
	for (const key of FEATURE_KEYS)
		out[key] = FEATURE_FLAGS[key].defaultEnabled;
	return out;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { user, currentOrganization } = useAuth();
	const [flags, setFlags] = useState<Record<FeatureKey, boolean>>(() => buildDefaultFlags());
	// Starts true so route guards can hold navigation until the first resolution.
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);

	const resolveFlags = (rows: FeatureFlagRow[], userId: string | undefined, orgId: string | undefined): Record<FeatureKey, boolean> => {
		const resolved = buildDefaultFlags();
		for (const key of FEATURE_KEYS) {
			const userRow = userId ? rows.find(r => r.feature_key === key && r.user_id === userId) : undefined;
			if (userRow) {
				resolved[key] = userRow.enabled;
				continue;
			}
			const orgRow = orgId ? rows.find(r => r.feature_key === key && r.organization_id === orgId) : undefined;
			if (orgRow)
				resolved[key] = orgRow.enabled;
		}
		return resolved;
	};

	const load = useCallback(async () => {
		if (!user) {
			setFlags(buildDefaultFlags());
			setLoading(false);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const orgId = currentOrganization?.id;
			const orFilter = orgId
				? `user_id.eq.${user.id},organization_id.eq.${orgId}`
				: `user_id.eq.${user.id}`;
			const { data, error: fetchError } = await (supabase as any)
				.from("feature_flags")
				.select("*")
				.or(orFilter);
			if (fetchError)
				throw fetchError;
			const rows = (data ?? []) as FeatureFlagRow[];
			setFlags(resolveFlags(rows, user.id, orgId));
		} catch (err: any) {
			setError(err instanceof Error ? err : new Error(String(err)));
			setFlags(buildDefaultFlags());
		} finally {
			setLoading(false);
		}
	}, [user, currentOrganization?.id]);

	useEffect(() => {
		load();
	}, [load]);

	const value: FeatureFlagsContextValue = useMemo(() => ({ flags, loading, error, refresh: load }), [flags, loading, error, load]);

	return (
		<FeatureFlagsContext.Provider value={value}>
			{children}
		</FeatureFlagsContext.Provider>
	);
};

export const useFeatureFlags = (): FeatureFlagsContextValue => {
	const ctx = useContext(FeatureFlagsContext);
	if (!ctx)
		throw new Error("useFeatureFlags must be used within a FeatureFlagsProvider");
	return ctx;
};

export const useFeatureFlag = (key: FeatureKey): boolean => {
	const { flags, error } = useFeatureFlags();
	if (error)
		return true;
	if (!(key in FEATURE_FLAGS))
		return true;
	return flags[key];
};
